// ACTA_FINANCIAL_PERIOD_DASHBOARD_V1
// ACTA_COMMERCIAL_DASHBOARD_RESPONSIVE_V2
import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import DashboardBusinessGrid, { DashboardPriorityAlert } from "@/components/dashboard/DashboardBusinessGrid";
import { THEME_COLORS } from "@/lib/themeColors";
import {
  CalendarBlank,
  CaretDown,
  CheckCircle,
  FileText,
  Target,
  TrendUp,
  UserPlus,
  X,
} from "@phosphor-icons/react";


function numberValue(value) {
  const parsed = Number(value || 0);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}


function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(
    numberValue(value)
  );
}


function formatCurrency(value) {
  return new Intl.NumberFormat(
    "fr-FR",
    {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }
  ).format(numberValue(value));
}


function toInputDate(value) {
  const date = value instanceof Date
    ? value
    : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");
  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}


function startOfMonth(value) {
  return new Date(
    value.getFullYear(),
    value.getMonth(),
    1
  );
}


function endOfMonth(value) {
  return new Date(
    value.getFullYear(),
    value.getMonth() + 1,
    0
  );
}


function addMonths(value, months) {
  const date = new Date(value);
  date.setMonth(date.getMonth() + months);
  return date;
}


function quickPeriod(key) {
  const today = new Date();
  const todayKey = toInputDate(today);

  if (key === "today") {
    return {
      mode: "exact",
      from: todayKey,
      to: todayKey,
      preset: key,
    };
  }

  if (key === "this-month") {
    return {
      mode: "between",
      from: toInputDate(startOfMonth(today)),
      to: toInputDate(endOfMonth(today)),
      preset: key,
    };
  }

  if (key === "previous-month") {
    const previous = addMonths(today, -1);

    return {
      mode: "between",
      from: toInputDate(startOfMonth(previous)),
      to: toInputDate(endOfMonth(previous)),
      preset: key,
    };
  }

  if (key === "last-3-months") {
    return {
      mode: "between",
      from: toInputDate(addMonths(today, -3)),
      to: todayKey,
      preset: key,
    };
  }

  if (key === "last-6-months") {
    return {
      mode: "between",
      from: toInputDate(addMonths(today, -6)),
      to: todayKey,
      preset: key,
    };
  }

  if (key === "current-year") {
    return {
      mode: "between",
      from: `${today.getFullYear()}-01-01`,
      to: `${today.getFullYear()}-12-31`,
      preset: key,
    };
  }

  return {
    mode: "between",
    from: toInputDate(addMonths(today, -6)),
    to: todayKey,
    preset: "last-6-months",
  };
}


export function createDefaultFinancialPeriod() {
  return quickPeriod("last-6-months");
}


export function financialPeriodRequest(period = {}) {
  return {
    dateMode: period.mode || "between",
    dateFrom: period.from || undefined,
    dateTo: period.to || undefined,
  };
}


function compactHumanDate(value, includeYear = true) {
  if (!value) return "";

  const date = new Date(`${value}T12:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(
    "fr-FR",
    {
      day: "numeric",
      month: "short",
      ...(includeYear ? { year: "numeric" } : {}),
    }
  ).replace(".", "");
}


function periodLabel(period = {}) {
  if (period.mode === "exact") {
    return compactHumanDate(period.from);
  }

  if (period.mode === "from") {
    return `Depuis le ${compactHumanDate(period.from)}`;
  }

  if (period.mode === "before") {
    return `Avant le ${compactHumanDate(period.to)}`;
  }

  const sameYear = String(period.from || "").slice(0, 4)
    === String(period.to || "").slice(0, 4);

  return `${compactHumanDate(period.from, !sameYear)} – ${compactHumanDate(period.to)}`;
}


function periodPresetShortLabel(period = {}) {
  const labels = {
    today: "Aujourd’hui",
    "this-month": "Ce mois",
    "previous-month": "Mois précédent",
    "last-3-months": "3 mois",
    "last-6-months": "6 mois",
    "current-year": "Année en cours",
    custom: "Personnalisée",
  };

  return labels[period.preset] || "";
}


function validatePeriod(period) {
  if (period.mode === "between") {
    if (!period.from || !period.to) {
      return "Renseignez une date de début et une date de fin.";
    }

    if (period.from > period.to) {
      return "La date de début doit précéder la date de fin.";
    }
  }

  if (
    (period.mode === "from" || period.mode === "exact")
    && !period.from
  ) {
    return "Renseignez la date de départ.";
  }

  if (
    period.mode === "before"
    && !period.to
  ) {
    return "Renseignez la date limite.";
  }

  return "";
}


function CommercialFilter({
  value,
  onChange,
  currentUser,
  commercialUsers,
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const defaultLabel =
    currentUser?.role === "manager"
      ? "Mon équipe"
      : "Tous les commerciaux";

  const currentUserId =
    String(currentUser?.id || "");

  const normalizedValue =
    String(value || "");

  const options = [
    {
      key: "all",
      value: "",
      label: defaultLabel,
    },

    ...(currentUserId
      ? [
          {
            key: "self",
            value: currentUserId,
            label: "Moi — utilisateur en cours",
          },
        ]
      : []),

    ...(commercialUsers || [])
      .filter(
        (entry) =>
          entry?.id
          && String(entry.id)
            !== currentUserId
      )
      .map((entry) => ({
        key: `owner-${entry.id}`,
        value: String(entry.id),
        label: entry.name,
      })),
  ];

  const selected =
    options.find(
      (option) =>
        option.value === normalizedValue
    )
    || options[0];

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    const handleOutside = (event) => {
      if (
        wrapperRef.current
        && !wrapperRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    document.addEventListener(
      "mousedown",
      handleOutside
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );

      document.removeEventListener(
        "mousedown",
        handleOutside
      );
    };
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="acta-commercial-filter relative min-w-0"
      data-testid="dashboard-team-filter-v2"
    >
      <button
        type="button"
        onClick={() =>
          setOpen((current) => !current)
        }
        className={`acta-dashboard-filter-trigger acta-commercial-filter-trigger ${
          open ? "is-open" : ""
        }`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Filtrer le dashboard commercial par commercial"
      >
        <span
          className="acta-dashboard-filter-icon"
          aria-hidden="true"
        >
          <UserPlus
            size={18}
            weight="duotone"
          />
        </span>

        <span className="min-w-0 flex-1 truncate text-left">
          {selected.label}
        </span>

        <CaretDown
          size={17}
          weight="bold"
          className={`acta-dashboard-filter-caret ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="acta-commercial-filter-panel"
          role="listbox"
          aria-label="Choisir le périmètre commercial"
          data-testid="dashboard-team-filter-panel-v2"
        >
          {options.map((option) => {
            const active =
              option.value
              === normalizedValue;

            return (
              <button
                key={option.key}
                type="button"
                role="option"
                aria-selected={active}
                className={`acta-commercial-filter-option ${
                  active
                    ? "is-active"
                    : ""
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span
                  className="acta-commercial-filter-option-icon"
                  aria-hidden="true"
                >
                  <UserPlus
                    size={18}
                    weight="duotone"
                  />
                </span>

                <span className="min-w-0 flex-1 truncate">
                  {option.label}
                </span>

                <span
                  className="acta-commercial-filter-check"
                  aria-hidden="true"
                >
                  {active && (
                    <CheckCircle
                      size={19}
                      weight="fill"
                    />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


function PeriodFilter({
  period,
  onChange,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const desktop =
      window.matchMedia("(min-width: 1024px)").matches;

    if (!open || desktop) {
      delete document.body.dataset.actaMobileOverlay;
      return undefined;
    }

    document.body.dataset.actaMobileOverlay =
      "financial-period";

    return () => {
      delete document.body.dataset.actaMobileOverlay;
    };
  }, [open]);
  const [draft, setDraft] = useState(period);
  const [customExpanded, setCustomExpanded] = useState(
    period.preset === "custom"
  );
  const [error, setError] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setDraft(period);
      setCustomExpanded(period.preset === "custom");
      setError("");
    }
  }, [
    open,
    period,
  ]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (
        window.matchMedia("(min-width: 1024px)").matches
        && wrapperRef.current
        && !wrapperRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutside
      );
    };
  }, [open]);

  const apply = () => {
    const validation = validatePeriod(draft);

    if (validation) {
      setError(validation);
      return;
    }

    onChange({
      ...draft,
      preset: draft.preset || "custom",
    });
    setOpen(false);
  };

  const quickRanges = [
    ["today", "Aujourd’hui"],
    ["this-month", "Ce mois"],
    ["previous-month", "Mois précédent"],
    ["last-3-months", "3 mois"],
    ["last-6-months", "6 mois"],
    ["current-year", "Année en cours"],
  ];

  return (
    <div
      className="relative"
      ref={wrapperRef}
      data-testid="dashboard-financial-period-filter"
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="acta-dashboard-filter-trigger acta-financial-period-trigger flex min-h-[50px] w-full items-center gap-3 rounded-xl border bg-[var(--do-bg-solid)] px-3 py-2 text-left shadow-sm transition hover:shadow-md lg:hidden"
        style={{
          borderColor: "var(--do-border)",
        }}
        aria-expanded={open}
      >
        <span className="acta-financial-period-trigger-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-xl lg:h-8 lg:w-8 lg:rounded-lg">
          <CalendarBlank
            size={21}
            weight="duotone"
          />
        </span>

        <span className="min-w-0 flex-1">
          <span
            className="block text-xs font-bold lg:hidden"
            style={{
              color: "var(--do-muted)",
            }}
          >
            Période financière
          </span>

          <strong
            className="mt-0.5 block truncate text-sm lg:mt-0 lg:text-xs"
            style={{
              color: "var(--do-ink)",
            }}
          >
            {periodLabel(period)}
          </strong>
        </span>

        {periodPresetShortLabel(period) && (
          <span
            className="acta-financial-period-preset-pill hidden shrink-0 lg:inline-flex"
          >
            {periodPresetShortLabel(period)}
          </span>
        )}

        <CaretDown
          size={18}
          weight="bold"
          className={`shrink-0 transition ${
            open ? "rotate-180" : ""
          }`}
          style={{
            color: "var(--do-ink)",
          }}
        />
      </button>

      <div
        className="acta-financial-period-compact hidden min-w-0 items-center lg:flex"
        data-testid="dashboard-financial-period-compact-v9"
      >
        {quickRanges.map(([key, label]) => {
          const active =
            period.preset === key;

          const compactLabel =
            key === "current-year"
              ? "Année"
              : label;

          return (
            <button
              key={key}
              type="button"
              className={`acta-financial-period-compact-option ${
                active ? "is-active" : ""
              }`}
              aria-pressed={active}
              onClick={() => {
                const next = quickPeriod(key);

                onChange(next);
                setDraft(next);
                setCustomExpanded(false);
                setError("");
                setOpen(false);
              }}
            >
              {compactLabel}
            </button>
          );
        })}

        <button
          type="button"
          className={`acta-financial-period-compact-option acta-financial-period-custom-trigger ${
            period.preset === "custom"
              ? "is-active"
              : ""
          }`}
          data-testid="dashboard-financial-period-custom-trigger"
          aria-expanded={open}
          onClick={() => {
            const today =
              toInputDate(new Date());

            setDraft({
              ...period,
              mode: "between",
              preset: "custom",
              from: period.from || today,
              to: period.to || today,
            });

            setCustomExpanded(true);
            setError("");
            setOpen((current) => !current);
          }}
        >
          <CalendarBlank
            size={14}
            weight="bold"
            aria-hidden="true"
          />

          <span>Personnalisé</span>
        </button>
      </div>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 backdrop-blur-[1px] lg:hidden"
            style={{ background: "var(--do-overlay)" }}
            onClick={() => setOpen(false)}
            aria-label="Fermer le filtre de période"
          />

          <section
            className="acta-financial-period-sheet fixed inset-x-0 bottom-0 z-[150] flex max-h-[calc(100dvh-5rem-env(safe-area-inset-top,0px))] w-full max-w-full flex-col overflow-hidden rounded-t-[30px] border bg-[var(--do-bg-solid)] shadow-2xl lg:absolute lg:inset-x-auto lg:bottom-auto lg:right-0 lg:top-[calc(100%+0.75rem)] lg:max-h-[min(82vh,760px)] lg:w-[480px] lg:rounded-3xl"
            style={{
              borderColor: "var(--do-border)",
            }}
            data-testid="dashboard-financial-period-panel"
          >
            <div className="acta-financial-period-header">
              <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-[var(--do-bg-2)] lg:hidden" />

              <div className="flex items-start justify-between gap-4">
              <div>
                <h2
                  className="font-display text-xl"
                  style={{
                    color: "var(--do-ink)",
                  }}
                >
                  Période financière
                </h2>

                <p
                  className="mt-1 text-xs leading-5"
                  style={{
                    color: "var(--do-muted)",
                  }}
                >
                  Choisissez une période rapide ou personnalisée.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="acta-financial-period-close flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition"
                aria-label="Fermer"
              >
                <X size={19} weight="bold" />
              </button>
              </div>
            </div>

            <div className="acta-financial-period-body">
              <div className="acta-financial-quick-section">
                <h3
                  className="text-sm font-black"
                  style={{ color: "var(--do-ink)" }}
                >
                  Périodes rapides
                </h3>

                <div className="acta-financial-quick-ranges mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {quickRanges.map(([key, label]) => {
                    const active = draft.preset === key;

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setDraft(quickPeriod(key));
                          setCustomExpanded(false);
                          setError("");
                        }}
                        className={`acta-financial-quick-range rounded-xl border px-3 py-3 text-xs font-black transition ${
                          active ? "is-active" : ""
                        }`}
                        aria-pressed={active}
                      >
                        <span
                          className="acta-financial-quick-range-icon"
                          aria-hidden="true"
                        >
                          <CalendarBlank
                            size={20}
                            weight="duotone"
                          />
                        </span>

                        <span className="acta-financial-quick-range-label">
                          {label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={`acta-financial-custom-card mt-5 ${
                customExpanded ? "is-expanded" : ""
              }`}>
                <div className="acta-financial-custom-desktop-heading hidden items-center gap-3 lg:flex">
                  <span
                    className="acta-financial-custom-icon"
                    aria-hidden="true"
                  >
                    <CalendarBlank
                      size={20}
                      weight="duotone"
                    />
                  </span>

                  <span>
                    Période personnalisée
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const nextExpanded = !customExpanded;
                    setCustomExpanded(nextExpanded);

                    if (nextExpanded) {
                      setDraft((current) => ({
                        ...current,
                        mode: "between",
                        preset: "custom",
                        from: current.from || toInputDate(new Date()),
                        to: current.to || toInputDate(new Date()),
                      }));
                    }

                    setError("");
                  }}
                  className={`acta-financial-custom-toggle flex w-full items-center gap-3 px-4 py-4 text-left text-sm font-black transition lg:hidden ${
                    draft.preset === "custom" ? "is-active" : ""
                  }`}
                  aria-expanded={customExpanded}
                  aria-controls="dashboard-financial-custom-dates"
                >
                  <span
                    className="acta-financial-custom-icon"
                    aria-hidden="true"
                  >
                    <CalendarBlank
                      size={20}
                      weight="duotone"
                    />
                  </span>
                  <span className="flex-1">Personnalisée</span>
                  <CaretDown
                    size={18}
                    weight="bold"
                    className={`transition ${
                      customExpanded ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  id="dashboard-financial-custom-dates"
                  className={`acta-financial-custom-collapse ${
                    customExpanded ? "is-open" : ""
                  }`}
                >
                  <div className="acta-financial-custom-collapse-inner">
                    <div className="acta-financial-custom-dates grid gap-4 p-4">
                  <label>
                    <span className="text-xs font-black" style={{ color: "var(--do-ink-soft)" }}>
                      Date de début
                    </span>
                    <input
                      type="date"
                      value={draft.from || ""}
                      onChange={(event) => {
                        setDraft((current) => ({
                          ...current,
                          mode: "between",
                          from: event.target.value,
                          preset: "custom",
                        }));
                        setError("");
                      }}
                      className="acta-financial-date-input mt-2 h-12 min-w-0 w-full rounded-xl border bg-[var(--do-bg-solid)] px-3 text-sm font-bold outline-none focus:border-[var(--do-border-strong)] focus:ring-2 focus:ring-[var(--do-accent)]"
                      style={{ borderColor: "var(--do-border)", color: "var(--do-ink)" }}
                    />
                  </label>

                  <label>
                    <span className="text-xs font-black" style={{ color: "var(--do-ink-soft)" }}>
                      Date de fin
                    </span>
                    <input
                      type="date"
                      value={draft.to || ""}
                      onChange={(event) => {
                        setDraft((current) => ({
                          ...current,
                          mode: "between",
                          to: event.target.value,
                          preset: "custom",
                        }));
                        setError("");
                      }}
                      className="acta-financial-date-input mt-2 h-12 min-w-0 w-full rounded-xl border bg-[var(--do-bg-solid)] px-3 text-sm font-bold outline-none focus:border-[var(--do-border-strong)] focus:ring-2 focus:ring-[var(--do-accent)]"
                      style={{ borderColor: "var(--do-border)", color: "var(--do-ink)" }}
                    />
                  </label>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <p className="mt-3 rounded-xl bg-[var(--do-accent-soft)] px-3 py-2 text-xs font-bold text-[var(--do-accent)]">
                  {error}
                </p>
              )}
            </div>


            <div className="acta-financial-period-footer">
              <button
                type="button"
                onClick={apply}
                className="acta-financial-apply h-12 w-full rounded-xl text-sm font-black text-[var(--do-on-accent)] transition"
              >
                Appliquer la période
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}


function DashboardOverviewKpi({
  title,
  value,
  subtitle,
  icon,
  color,
  formatter = formatNumber,
  testId,
  to,
}) {
  const content = (
    <Card
      variant="acta"
      className={`dashboard-overview-kpi-card min-h-[104px] min-w-0 overflow-hidden rounded-2xl border px-2.5 py-3 shadow-sm sm:px-4 sm:py-3 ${
        to ? "is-clickable" : ""
      }`}
      data-testid={testId}
    >
      <div className="dashboard-overview-kpi-content flex h-full min-w-0 items-center gap-2 sm:gap-4">
        {icon && (
          <span
            className="dashboard-overview-kpi-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-full sm:h-14 sm:w-14"
            style={{
              color,
              backgroundColor: `${color}10`,
            }}
          >
            {React.createElement(icon, {
              size: 28,
              weight: "duotone",
            })}
          </span>
        )}

        <div className="dashboard-overview-kpi-body min-w-0 flex-1">
          <div
            className="dashboard-overview-kpi-title flex min-w-0 items-center gap-1 text-xs font-black sm:gap-2 sm:text-sm"
            style={{
              color: "var(--do-ink-soft)",
            }}
          >
            <span className="truncate">
              {title}
            </span>

            {to && (
              <ArrowRight
                size={14}
                weight="bold"
                className="dashboard-overview-kpi-arrow shrink-0"
                aria-hidden="true"
              />
            )}
          </div>

          <strong
            className="dashboard-overview-kpi-value mt-2 block min-w-0 whitespace-nowrap tabular-nums text-[clamp(1.05rem,5.2vw,1.35rem)] font-extrabold leading-none tracking-tight sm:text-[30px]"
            style={{
              color: "var(--do-ink)",
            }}
          >
            {formatter(value)}
          </strong>

          <p
            className="dashboard-overview-kpi-subtitle mt-2 truncate text-[10px] sm:text-xs"
            style={{
              color: "var(--do-muted)",
            }}
          >
            {subtitle}
          </p>
        </div>
      </div>
    </Card>
  );

  if (!to) {
    return content;
  }

  return (
    <Link
      to={to}
      className="dashboard-overview-kpi-link block h-full min-w-0 rounded-2xl"
      aria-label={`${title} — ouvrir le pipeline`}
    >
      {content}
    </Link>
  );
}

export default function FinancialPeriodDashboard({
  dashboard,
  period,
  onPeriodChange,
  canFilterTeam,
  ownerUserId,
  onOwnerUserIdChange,
  commercialUsers,
  currentUser,
  todayMeetings,
}) {
  const commercial =
    dashboard?.commercial || {};
  const financial =
    dashboard?.financialPeriod || {};
  const metrics =
    financial?.metrics || {};
  const periodPipeline =
    financial?.pipeline
    || commercial?.pipeline
    || {};
  const confirmedRevenue =
    metrics.confirmedRevenueAmount
    ?? commercial.confirmedRevenueAmount
    ?? 0;
  // ACTA_DASHBOARD_FORECAST_SINGLE_SOURCE_V2
  // Le CA prévisionnel vient du même snapshot que le Pipeline.
  // Finalisation reste donc comptabilisée jusqu'au passage en Gagnée.
  const openOpportunities =
    metrics.forecastCount
    ?? commercial.openOpportunityCount
    ?? 0;
  const potentialRevenue =
    metrics.forecastAmount
    ?? commercial.forecastAmount
    ?? 0;
  const wonCount = numberValue(
    periodPipeline?.won?.count
  );
  const lostCount = numberValue(
    periodPipeline?.lost?.count
  );
  const closedBusinessCount =
    wonCount
    + lostCount;
  return (
    <div className="w-full max-w-none px-3 py-3 sm:px-4 lg:px-4 lg:py-3">
      {/* Desktop : quatre KPI et quatre blocs métier, sans Carte ni Statistiques. */}
      <section
        className="dashboard-enterprise-desktop hidden min-w-0 space-y-4 pb-5 lg:block"
        data-testid="dashboard-enterprise-operations"
        data-layout="commercial-four-blocks-v1"
      >
        <header
          className={`dashboard-filter-bar grid min-h-[46px] w-full items-center gap-3 ${
            canFilterTeam ? "has-team-filter" : "period-only"
          }`}
          data-testid="dashboard-enterprise-header"
        >
          {canFilterTeam && (
            <CommercialFilter
              value={ownerUserId}
              onChange={onOwnerUserIdChange}
              currentUser={currentUser}
              commercialUsers={commercialUsers}
            />
          )}
          <div className="min-w-0">
            <PeriodFilter period={period} onChange={onPeriodChange} />
          </div>
        </header>
        <section className="grid min-w-0 grid-cols-2 gap-3 xl:grid-cols-4" data-testid="dashboard-commercial-overview">
          <DashboardOverviewKpi title="Opportunités" value={openOpportunities}
            subtitle="Sur la période sélectionnée" icon={Target} color={THEME_COLORS.primary}
            testId="dashboard-kpi-opportunities" to="/app/commerce/pipeline" />
          <DashboardOverviewKpi title="CA potentiel" value={potentialRevenue}
            subtitle="Sur la période sélectionnée" icon={TrendUp} color={THEME_COLORS.success}
            formatter={formatCurrency} testId="dashboard-kpi-potential-revenue" to="/app/commerce/pipeline" />
          <DashboardOverviewKpi title="CA réalisé" value={confirmedRevenue}
            subtitle="Sur la période sélectionnée" icon={CheckCircle} color={THEME_COLORS.success}
            formatter={formatCurrency} testId="dashboard-kpi-realized-revenue" to="/app/commerce/pipeline" />
          <DashboardOverviewKpi title="Affaires clôturées" value={closedBusinessCount}
            subtitle={`${formatNumber(wonCount)} gagnée(s) · ${formatNumber(lostCount)} perdue(s)`}
            icon={FileText} color={THEME_COLORS.orange} testId="dashboard-kpi-closed-business" />
        </section>
        <DashboardBusinessGrid dashboard={dashboard} todayMeetings={todayMeetings} />
        <DashboardPriorityAlert overdueFollowups={commercial.overdueFollowupsCount} />
      </section>
    </div>
  );
}
